import React, { useMemo } from "react";
import { withRouter } from "react-router";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { usePatientWalletSummary } from "./hooks/usePatientWalletSummary";
import { uiNotification } from "../../../../../services/UINotificationService";
import { tCommon } from "../../../../../i18n/useAppTranslation";
import { Table } from "../../../../../shared/Table/Table";
import { ReportStats } from "../../../../../boxes/ReportStats/ReportStats";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { LoadMoreObserver } from "../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import Header from "../../../components/Header";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const PatientWalletSummary = ({ history }) => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();
  // Prepare API parameters
  const requestParams = useMemo(() => {
    const params = {
      ...apiPeriod,
    };

    if (selectedClinic?.value?.value !== 0) {
      params["clinic_ids[]"] = selectedClinic.value.value;
    }

    return params;
  }, [apiPeriod, selectedClinic]);

  // Fetch data
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = usePatientWalletSummary(requestParams, {
    staleTime: 0,
    cacheTime: 0,
    keepPreviousData: true,
    onError: () => {
      uiNotification.error(tCommon("error.getReports"));
    },
  });

  // Prepare download params (remove pagination for export)
  const downloadParams = useMemo(() => {
    const params = { ...requestParams };
    delete params.page;
    delete params.limit;
    return params;
  }, [requestParams]);

  // Handle row click to navigate to detail view
  const handleRowClick = (row) => {
    if (row.patient_id) {
      history.push(
        `/refactor-business-insight/sales/patient_wallet_liability/${row.patient_id}`,
      );
    }
  };

  // Extract data from paginated structure
  const reportData = useMemo(() => {
    return data?.pages?.map((page) => page.reportData).flat() || [];
  }, [data]);

  const apiReportHeader = useMemo(() => {
    return data?.pages?.[0]?.reportHeader || {};
  }, [data]);

  const apiSummary = useMemo(() => {
    const summary = data?.pages?.[0]?.summary || {};
    return summary;
  }, [data]);

  const apiClinics = useMemo(() => {
    return data?.pages?.[0]?.clinics || [];
  }, [data]);

  // Prepare clinics for header
  const clinics = useMemo(() => {
    const clinicOptions = [{ label: "All Clinics", value: 0 }];
    if (apiClinics?.length) {
      clinicOptions.push(
        ...apiClinics.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      );
    }
    return clinicOptions;
  }, [apiClinics]);

  // Prepare table columns and data
  const tableColumns = useMemo(() => {
    if (!apiReportHeader) return [];

    return [
      {
        data: apiReportHeader.patient_name || "Patient",
        accessor: "patient_name",
      },
      { data: apiReportHeader.email || "Email", accessor: "email" },
      {
        data: apiReportHeader.dollar_credits_balance || "Dollar Credits",
        accessor: "dollar_credits_balance",
      },
      {
        data: apiReportHeader.prepaid_services_balance || "Prepaid Services",
        accessor: "prepaid_services_balance",
      },
      {
        data: apiReportHeader.non_cash_credits_balance || "Non-Cash Credits",
        accessor: "non_cash_credits_balance",
      },
      {
        data: apiReportHeader.total_outstanding || "Total Outstanding",
        accessor: "total_outstanding",
      },
      {
        data: apiReportHeader.last_activity_date || "Last Activity",
        accessor: "last_activity_date",
      },
    ];
  }, [apiReportHeader]);

  const tableData = useMemo(() => {
    if (!reportData) return [];

    return reportData.map((row) => ({
      ...row,
      patient_name: row.patient_name || "-",
      email: row.email || "-",
      dollar_credits_balance: row.dollar_credits_balance || "$0.00",
      prepaid_services_balance: row.prepaid_services_balance || "$0.00",
      non_cash_credits_balance: row.non_cash_credits_balance || "$0.00",
      total_outstanding: row.total_outstanding || "$0.00",
      last_activity_date: row.last_activity_date || "-",
    }));
  }, [reportData]);

  // Prepare stats
  const stats = useMemo(() => {
    if (!apiSummary) return [];

    const statsArray = [
      {
        id: "total_patients",
        name: "Total Patients",
        data: apiSummary.total_patients || 0,
      },
      {
        id: "total_dollar_credits",
        name: "Total Dollar Credits",
        data: apiSummary.total_dollar_credits || "$0.00",
      },
      {
        id: "total_prepaid_services",
        name: "Total Prepaid Services",
        data: apiSummary.total_prepaid_services || "$0.00",
      },
      {
        id: "total_non_cash_credits",
        name: "Total Non-Cash Credits",
        data: apiSummary.total_non_cash_credits || "$0.00",
      },
      {
        id: "total_outstanding_liability",
        name: "Total Outstanding Liability",
        data: apiSummary.total_outstanding_liability || "$0.00",
      },
      {
        id: "total_current_liabilities",
        name: "Total Current Liabilities",
        data: apiSummary.total_current_liabilities || "$0.00",
      },
    ];

    return statsArray;
  }, [apiSummary]);

  return (
    <div className="flex-col gap-16">
      <Header
        title="Patient Wallet Liability Summary"
        clinics={clinics}
        selectedClinic={selectedClinic}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getPatientWalletSummary()}
        requestParams={downloadParams}
      />

      <ReportStats data={stats} isLoading={isLoading} />

      <Box>
        {isLoading && (
          <div className={classes.tableContainer}>
            <Skeleton count={8} height={40} borderRadius="0px" />
          </div>
        )}

        {error && (
          <Empty
            position="center"
            title="API Error"
            description={`Failed to load patient wallet data: ${
              error.message || "Unknown error"
            }`}
          />
        )}

        {!isLoading && !error && tableData.length > 0 && (
          <div className={classes.tableContainer} style={{ overflowX: "auto" }}>
            <Table
              cols={tableColumns}
              data={tableData}
              colClassName="bordersides"
              headColClassName={classes.tableHeaderColumn}
              onRowClick={handleRowClick}
            />
            {hasNextPage && (
              <LoadMoreObserver
                noPaddingBottom
                loadMore={fetchNextPage}
                isLoading={isFetchingNextPage}
                hasMore={hasNextPage}
              />
            )}
          </div>
        )}

        {!isLoading && !error && tableData.length === 0 && (
          <Empty
            position="center"
            title="No patient wallet data found"
            description="Try adjusting your date range or clinic selection"
          />
        )}
      </Box>
    </div>
  );
};

export default withRouter(PatientWalletSummary);
